/** /notification_blocks/ — cross-tenant Notification_Block catalog
 *  (list + CRUD + used_by).
 *
 * A notification block is a named, typed value (template / ruleset / dotty
 * path / JSONPath / function / constant / inline rule / ephemeral tag)
 * referenced in notification templates as `block.<codename>`. `tour_operator`
 * is nullable: a null tenant is a system-wide block usable by every operator.
 *
 * Two types carry structured payloads beyond a plain `definition` string:
 * `rule` stores an inline-rules JSON object in `definition`, and `tag` stores
 * an optional condition there plus a `tags` chip list (the same chip contract
 * feature-flag tag conditions use, so `@/components/TagConditionInput`'s
 * converters apply verbatim).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Notification_Block has no optimistic-concurrency
 * token, so writes are last-writer-wins (no conflict banner, like regions).
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** Block kinds, in display order. `definition` is interpreted per type. */
export const BLOCK_TYPES = [
  { value: 'template', label: 'Template' },
  { value: 'ruleset', label: 'Ruleset' },
  { value: 'dotty', label: 'Dotty path' },
  { value: 'jsonpath', label: 'JSONPath' },
  { value: 'function', label: 'Function' },
  { value: 'constant', label: 'Constant' },
  { value: 'rule', label: 'Inline rule' },
  { value: 'tag', label: 'Ephemeral tag' },
] as const;
export type NotificationBlockType = (typeof BLOCK_TYPES)[number]['value'];

/** Block-type values as a tuple, for the form's zod enum — derived so a new
 *  entry in BLOCK_TYPES can never be silently rejected by the schema. */
export const BLOCK_TYPE_VALUES = BLOCK_TYPES.map((t) => t.value) as unknown as [
  NotificationBlockType,
  ...NotificationBlockType[],
];

/** Template rendering engine — only meaningful for the `template` type. */
export const TEMPLATE_LANGUAGES = [
  { value: 'django', label: 'Django' },
  { value: 'jinja2', label: 'Jinja2' },
] as const;
export type TemplateLanguage = (typeof TEMPLATE_LANGUAGES)[number]['value'];

/** What to do when evaluation errors / yields nothing. */
export const ERROR_MODES = [
  { value: 'soft', label: 'Soft — log & fall back to default' },
  { value: 'hard', label: 'Hard — abort the notification' },
] as const;
export type ErrorMode = (typeof ERROR_MODES)[number]['value'];

/** Delivery channels a `channel_overrides` entry may target (`template` type only). */
export const OVERRIDE_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'chat', label: 'Chat' },
  { value: 'airbnb', label: 'Airbnb' },
] as const;
export type OverrideChannel = (typeof OVERRIDE_CHANNELS)[number]['value'];

/** Delivery target formats a `template` block resolves a definition for.
 *  html = the base `definition`; markdown/plaintext fall back to it when empty. */
export const BLOCK_FORMATS = [
  { value: 'html', label: 'HTML (default)' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'plaintext', label: 'Plaintext' },
] as const;
export type BlockFormat = (typeof BLOCK_FORMATS)[number]['value'];

export interface NotificationBlockListItem {
  id: number;
  /** Owning tenant. Null for system-wide blocks (`Notification_Block
   *  .tour_operator` is nullable, serialised as `tenant: null`). */
  tenant: TenantRef | null;
  /** Referenced in templates as `block.<codename>` (identifier grammar). */
  codename: string;
  name: string;
  description: string;
  type: NotificationBlockType;
  /** Meaning depends on `type` (template source / ruleset spec / dotty path /
   *  JSONPath / function name / constant literal). */
  definition: string;
  /** `template` type only: source used when the target format is markdown;
   *  empty → the base `definition`. */
  definition_markdown: string;
  /** `template` type only: source used when the target format is plaintext;
   *  empty → the base `definition`. */
  definition_plaintext: string;
  /** `template` type only: per-channel definition overrides. A non-empty
   *  override wins over the format-specific definitions for that channel. */
  channel_overrides: Partial<Record<OverrideChannel, string>>;
  template_language: TemplateLanguage;
  /** `constant` type only: parse `definition` as JSON. */
  is_json: boolean;
  /** Fallback used when evaluation errors or yields nothing. */
  default_value: string | null;
  error_mode: ErrorMode;
  enabled: boolean;
  /** Feature-registry codename gating this block (renders only while the
   *  feature is enabled at every applicable level); null = unconditional. */
  feature: string | null;
  /** Provenance from `manage.py pack_seed`; null for a hand-authored block.
   *  `managed` = the row still matches what the pack wrote, so a future seed
   *  updates it in place. `diverged` = it has been edited since, so the seed
   *  reports it and leaves it alone. Editing a managed block opts it out of the
   *  pack's content updates, which is worth telling someone before they do it. */
  pack: { version: string | null; status: 'managed' | 'diverged' } | null;
  /** How this row relates to the same codename in the other scope (D4). A
   *  reference to a SYSTEM block resolves to the delivering tenant's own block
   *  of that codename, so `shadowed_by` counts the tenants who will NOT see an
   *  edit made to the system row. */
  shadowing: { overrides_system: boolean; shadowed_by: number };
  created_ts: string | null;
  updated_ts: string | null;
}

export interface NotificationBlockDetail extends NotificationBlockListItem {
  /** Reference counts of the PROTECTed FKs that block a delete (409). */
  used_by: {
    notifications: number;
    lists: number;
  };
  /** `tag` type only: the block's tag references, position-ordered. Read chips
   *  are the shared contract — feed them to `fromWireChips` (cast to
   *  `WireTagChipRead[]`) exactly like a feature-flag tag condition. */
  tags?: NotificationBlockTagChipRead[];
}

/** Read shape of one tag chip: a resolved concrete tag XOR a stored spec.
 *  Mirrors `WireTagChipRead` from `@/components/TagConditionInput`. */
export type NotificationBlockTagChipRead =
  | {
      tag: {
        id: number;
        name: string;
        is_deleted?: boolean;
        category: { id: number; code: string; name: string };
        tenant: { id: number; code: string; name: string } | null;
      };
    }
  | { category_code: string; tag_name: string };

/** Write shape of one tag chip (note `tag`, not `tag_id`). */
export type NotificationBlockTagChipWrite =
  | { tag: number }
  | { category_code: string; tag_name: string };

/** Create/update body. `codename` + `type` required on create; `tour_operator`
 *  null = system-wide. Type-specific validation runs server-side. */
export interface NotificationBlockWrite {
  tour_operator?: number | null;
  codename?: string;
  name?: string;
  description?: string;
  type?: NotificationBlockType;
  definition?: string;
  definition_markdown?: string;
  definition_plaintext?: string;
  channel_overrides?: Partial<Record<OverrideChannel, string>>;
  template_language?: TemplateLanguage;
  is_json?: boolean;
  default_value?: string | null;
  error_mode?: ErrorMode;
  enabled?: boolean;
  feature?: string | null;
  /** `tag` type only: full-list replace of the block's tag references (omit to
   *  keep the stored ones, `[]` clears them). Non-empty on any other type is
   *  rejected with a 400. */
  tags?: NotificationBlockTagChipWrite[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** codename + name + description + definition (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' / 'null' for system-wide blocks (no tenant). */
  tour_operator_id?: TenantIdsParam;
  type?: NotificationBlockType;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.type) usp.set('type', params.type);
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(
  params: ListParams = {},
): Promise<PaginatedResponse<NotificationBlockListItem>> {
  return apiGet<PaginatedResponse<NotificationBlockListItem>>(
    `/notification_blocks/${toQuery(params)}`,
  );
}

export function retrieve(id: number): Promise<NotificationBlockDetail> {
  return apiGet<NotificationBlockDetail>(`/notification_blocks/${id}/`);
}

export function create(body: NotificationBlockWrite): Promise<NotificationBlockDetail> {
  return apiPost<NotificationBlockDetail>('/notification_blocks/', body);
}

export function update(
  id: number,
  body: NotificationBlockWrite,
): Promise<NotificationBlockDetail> {
  return apiPatch<NotificationBlockDetail>(`/notification_blocks/${id}/`, body);
}

/** Copy a block into a tenant, creating a D4 shadow.
 *
 *  Needs only `console_notification_blocks_manage` — cloning is how you
 *  customise a standard block WITHOUT editing the shared row every other tenant
 *  renders. 409 if that tenant already has this codename. */
export function clone(id: number, tourOperatorId: number): Promise<NotificationBlockDetail> {
  return apiPost<NotificationBlockDetail>(`/notification_blocks/${id}/clone/`, {
    tour_operator: tourOperatorId,
  });
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/notification_blocks/${id}/`);
}

// ---- test evaluation (visualized testing) ----

export type EntityKind = 'gtu' | 'order' | 'user' | null;

export interface SupportedEvent {
  code: string;
  family: string;
  /** Which entity id can supply a context (gtu/order/user), or null. */
  entity_kind: EntityKind;
}

export function listSupportedEvents(): Promise<SupportedEvent[]> {
  return apiGet<{ events: SupportedEvent[] }>('/notification_blocks/events/').then(
    (r) => r.events,
  );
}

export type EvaluateContext =
  | { mode: 'json'; json: string }
  | { mode: 'event_delivery_log'; event_delivery_log_id: string }
  | { mode: 'entity'; entity_id: number };

export interface EvaluateRequest {
  event: string;
  block: NotificationBlockWrite;
  context: EvaluateContext;
  /** Delivery channel resolving a `template` block's definition (a channel
   *  override wins); omit for the base evaluation. */
  channel?: OverrideChannel;
  /** Target format picking a `template` block's definition when no channel
   *  override applies. Default html (the base definition). */
  format?: BlockFormat;
}

export interface EvaluateResult {
  ok: boolean;
  result: unknown;
  error: string | null;
  context_keys: string[];
  fully_resolved?: boolean;
}

export function evaluateBlock(body: EvaluateRequest): Promise<EvaluateResult> {
  return apiPost<EvaluateResult>('/notification_blocks/evaluate/', body);
}
